import React from 'react';
import { DashboardASINData } from '@/types/amazonIndexChecker';

interface ASINCardProps {
  asin: DashboardASINData;
  onClick: (company: string, asin: string) => void;
}

const ASINCard: React.FC<ASINCardProps> = ({ asin, onClick }) => {
  const handleClick = () => {
    onClick(asin.company, asin.asin);
  };

  // Indexed count is always good, so always green
  const getIndexedCountColor = () => {
    return 'text-green-600 dark:text-green-400';
  };

  // 5-tier percentage badge color system (higher = better)
  const getStatusBadgeColor = (percentage: number) => {
    if (percentage >= 75) return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
    if (percentage >= 50) return 'bg-lime-100 text-lime-800 dark:bg-lime-900/20 dark:text-lime-400';
    if (percentage >= 25) return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
    if (percentage >= 10) return 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400';
    return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).format(date);
  };

  const getScheduleStatus = () => {
    if (!asin.nextScheduledCheck) return null;
    
    const now = new Date();
    const nextUpdate = new Date(asin.nextScheduledCheck);
    const isOverdue = nextUpdate < now;
    
    return {
      isOverdue,
      text: isOverdue ? 'Overdue' : 'Scheduled',
      color: isOverdue ? 'text-red-600' : 'text-blue-600'
    };
  };

  const scheduleStatus = getScheduleStatus();

  return (
    <div
      className="bg-white dark:bg-gray-800 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 cursor-pointer border border-gray-200 dark:border-gray-700 p-4"
      onClick={handleClick}
    >
      {/* Header with Company and ASIN */}
      <div className="flex justify-between items-start mb-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">
            {asin.company}
          </h3>
          <p className="text-lg font-bold text-gray-900 dark:text-white mt-1">
            {asin.asin}
          </p>
        </div>
        <div className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(asin.percentageIndexed)}`}>
          {Math.round(asin.percentageIndexed)}% Indexed
        </div>
      </div>

      {/* Product Title */}
      {asin.productTitle && (
        <div className="mb-3">
          <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-2 leading-relaxed">
            {asin.productTitle}
          </p>
        </div>
      )}

      {/* Product Image */}
      {asin.imageUrl && (
        <div className="mb-3 flex justify-center">
          <img 
            src={asin.imageUrl} 
            alt={asin.productTitle || asin.asin}
            className="w-16 h-16 object-cover rounded-md border border-gray-200"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
            }}
          />
        </div>
      )}

      {/* Metrics */}
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div className="text-center">
          <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Terms Checked</p>
          <p className="text-lg font-bold text-gray-900 dark:text-white">{asin.termsCount}</p>
        </div>
        <div className="text-center">
          <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Indexed</p>
          <p className={`text-lg font-bold ${getIndexedCountColor()}`}>
            {asin.indexedCount}
          </p>
        </div>
      </div>

      {/* Last Updated & Next Update - Combined Row */}
      <div className="border-t pt-2">
        <div className="grid grid-cols-2 gap-2 text-center">
          {/* Last Updated */}
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400">Last Updated</p>
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {formatDate(new Date(asin.lastUpdated))}
            </p>
          </div>
          
          {/* Next Update */}
          <div>
            {asin.nextScheduledCheck && scheduleStatus ? (
              <>
                <p className="text-xs text-gray-500 dark:text-gray-400">Next Update</p>
                <p className={`text-sm font-medium ${scheduleStatus.color}`}>
                  {formatDate(new Date(asin.nextScheduledCheck))}
                </p>
              </>
            ) : (
              <>
                <p className="text-xs text-gray-500 dark:text-gray-400">Next Update</p>
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  Not Scheduled
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ASINCard;